import {Injectable} from "@angular/core";
import {IReferenceTextData, InterventionStatus} from "./recording-comparison.service";
import {CssSentence} from "../../content/content-view.objects";
import {IAudioData} from "../audio/microphone.service";
import {MicrophoneCheckInterventionState} from "./recording.interventions.state";
import {ReviewWordColor, ReviewWordReason, CompletionState} from "../usermodel/usermodel.objects";

export interface WordRecordData {
    id: string; // wordId - CssWord unique id
    duration: {
        durationTime: number;
    };
    audioIntervention: boolean;
    highlightIntervention: boolean;
    wordId: string;
    word: string;
    wordColor: ReviewWordColor;
    confidenceLevel: string; // high: "High",medium: "Medium",low: "Low" - assume "" for v2 - redundant/not used
    reviewReason: ReviewWordReason; // "NotRecognized", "NotConfident", "NoNeed" - assume "" for v2 - redundant/not used
    glossary: string; // getGlossaryTextByWordId why need this here? // assume "" for v2
}

export interface RecordingSessionData {
    audioFileContent: string;
    audioLength: number;
    audioType: string;
    duration: {
        durationTime: number;
    };
    sentenceId: string;
    textSelection: {
        wordStart: string; // wordId
        wordEnd: string;
        textHtml: string; // leave empty for v2?
        wordSelection: number; // always 0?
    };
    wordRecordSessionList: WordRecordData[];
}
// TODO: storyCompletionState, recordCompletionState, bookSessionId, and httpSessionId rely on outside data. start
// sentence also
export interface SelectionStateData {
    storyCompletionState: CompletionState; // one state is "Incomplete"
    recordCompletionState: CompletionState; // one state is "Completed"; same list as above, or different?
    currentPageId: number; // always 1 for bwd compatibility
}

export interface BookSessionData {
    // bookSessionId: string;
    recordReadingSessionList: RecordingSessionData[];
    learnerStory: SelectionStateData;
}

@Injectable()
export class RecordingObjects {
    // need to have a queue of wordRecordLists in case encoding is slow coming back
    private _wordRecordListQueue: WordRecordData[][];

    constructor() {
        this._wordRecordListQueue = [];

    }

    // was createWordSessionList
    public addWordRecordData(sentenceData: IReferenceTextData[]): void {
        let wordRecordList: WordRecordData[] = [];

        sentenceData.forEach((sentenceItem) => {
            // reviewReason, wordId, and confidenceLevel not used/not needed as far as we know, but have to put in
            // placeholders or MSL will not accept the upload.
            let reviewReason: ReviewWordReason = (sentenceItem.wordColor === ReviewWordColor.TRY_AGAIN)
                ? ReviewWordReason.TRY_AGAIN :
                                                 (sentenceItem.wordColor === ReviewWordColor.GREAT_JOB)
                                                     ? ReviewWordReason.GREAT_JOB :
                                                 ReviewWordReason.NOT_CONFIDENT;
            let wordRecordData: WordRecordData = {
                id: sentenceItem.word.id,
                duration: {durationTime: sentenceItem.elapsedTime},
                audioIntervention: (sentenceItem.interventionStatus === InterventionStatus.AUDIO),
                highlightIntervention: (sentenceItem.interventionStatus === InterventionStatus.VISUAL),
                wordId: sentenceItem.word.id,
                word: this.escapeUnicodeForHtml(sentenceItem.word.word),
                wordColor: sentenceItem.wordColor,
                confidenceLevel: "High",
                reviewReason: reviewReason,
                glossary: ""
            };

            wordRecordList.push(wordRecordData);
        });
        this._wordRecordListQueue.push(wordRecordList);
    };

    // was createWordSessionList
    public addDebugWordRecordData(sentence: CssSentence, wordDuration: number): void {
        let wordRecordList: WordRecordData[] = [];

        sentence.words.forEach((word) => {
            // reviewReason, wordId, and confidenceLevel not used/not needed as far as we know, but have to put in
            // placeholders or MSL will not accept the upload.
            let reviewReason: ReviewWordReason = ReviewWordReason.GREAT_JOB;
            let wordRecordData: WordRecordData = {
                id: word.id,
                duration: {durationTime: wordDuration},
                audioIntervention: false,
                highlightIntervention: false,
                wordId: word.id,
                word: this.escapeUnicodeForHtml(word.word),
                wordColor: ReviewWordColor.GREAT_JOB,
                confidenceLevel: "High",
                reviewReason: reviewReason,
                glossary: ""
            };

            wordRecordList.push(wordRecordData);
        });
        this._wordRecordListQueue.push(wordRecordList);
    };

    private escapeUnicodeForHtml(s: string): string {
        return s.replace(/[\u007f-\uffff]/g,
                         c => "&#x" + ("0000" + c.charCodeAt(0).toString(16)).slice(-4) + ";"
        );
    }

    // was clearWordSessionList
    public clearWordRecordListQueue(): void {
        this._wordRecordListQueue = [];
    }

    // was getWordSessionList
    public shiftWordRecordList(): WordRecordData[] {
        return this._wordRecordListQueue.shift();
    }


    public createUploadData(sentence: CssSentence, audioData: IAudioData, isEndOfSelection: boolean): BookSessionData {

        let bookSessionData: BookSessionData = {
            recordReadingSessionList: this.createRecordReadingSessionList(sentence, audioData),
            learnerStory: this.createSelectionStateData(sentence, isEndOfSelection)
        };

        return bookSessionData;
    }

    // was createSessionList

    public createRecordReadingSessionList(sentence: CssSentence, audioData: IAudioData): RecordingSessionData[] {
        let recordReadingSessionList: RecordingSessionData[] = [];
        // seems we only ever upload 1 sentence worth at a time
        let wordRecordList: WordRecordData[] = this.shiftWordRecordList();
        recordReadingSessionList[0] = {
            audioFileContent: audioData.audio,
            audioLength: audioData.audio.length, // length of base64 array
            audioType: audioData.type,
            duration: {
                durationTime: audioData.length
            },
            sentenceId: sentence.id,
            textSelection: {
                textHtml: "", // TODO: build HTML if needed; is used in selection detail report but depends on any
                              // schema rework decision
                wordStart: wordRecordList[0].wordId,
                wordEnd: wordRecordList[wordRecordList.length - 1].wordId,
                wordSelection: 0
            },
            wordRecordSessionList: wordRecordList

        };
        return recordReadingSessionList;
    }

    // was createLearnerSelection
    public createSelectionStateData(sentence: CssSentence, isEndOfSelection: boolean): SelectionStateData {
        let storyCompletionState: CompletionState = CompletionState.INCOMPLETE;
        // TODO: from V1 code, it seems storyCOmpletionState is only ever set to incomplete if starting a new
        // selection; otherwise not set?
        if (isEndOfSelection) {
            // TODO: need to check if completing this recording completes selection.  more usermodel state? or does
            // server take care of this?

        }
        return {
            storyCompletionState: storyCompletionState,
            recordCompletionState: isEndOfSelection ? CompletionState.COMPLETED : CompletionState.INCOMPLETE,
            currentPageId: 1
        };
    }

    /* From V1 code:
     if (options.sentenceList[0].currentSentenceId === 1) {
     // TODO: why is this logic here?  Is this for repeated selections?
     obj.storyCompletionState = "Incomplete";
     }
     */

}

export class MicrophoneCheckData {

    public isMicrophoneCheck: boolean = false;
    public microphoneCheckSentence: CssSentence = null;
    public microphoneCheckCorrectCount: number = 0;
    public microphoneCheckInterventionState: MicrophoneCheckInterventionState;
    public microphoneCheckPassed: boolean = false;
    public microphoneCheckInitiated: boolean = false;
    public temporarySentence: CssSentence = null;

    public start(sentence: CssSentence, interventionState: MicrophoneCheckInterventionState): void {

        this.microphoneCheckPassed = false;
        this.isMicrophoneCheck = true;
        this.microphoneCheckCorrectCount = 0;
        this.microphoneCheckSentence = sentence;
        this.microphoneCheckInterventionState = interventionState;
        this.microphoneCheckInterventionState.start();
    }

    public stop(): void {

        this.isMicrophoneCheck = false;
        this.microphoneCheckPassed = false;
        this.microphoneCheckCorrectCount = 0;
        this.microphoneCheckSentence = null;

        if (this.microphoneCheckInterventionState) {
            this.microphoneCheckInterventionState.stop();
        }
    }

}
